import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GetUserDto } from '@modeso/aumico-lib-users-fe';
import {
    AccountType,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import { Observable, map } from 'rxjs';
import { v4 as uuidV4 } from 'uuid';
import { DueDates } from '../../../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/dueDate.interface';
import { IAumMenuItem } from '../../../../../../../../aumico-lib-core-fe/src/lib';
import { isSubfli } from '../../../../../../../../aumico-lib-templates-fe/src/lib';
import { ClosingEntry, FcpModel, FcpTreeItemType } from '../../domain/models/fcp.model';
import { ClosingEntryAccount } from '../../domain/models/fcpClosingEntryAccount.model';
import { FcpTreeItem } from '../../domain/models/fcpTreeItem.model';
import { addClosingEntry, deleteClosingEntry, updateClosingEntry } from '../../domain/reducers/fcp.actions';
import { FcpFeatureState, selectActiveTestItem, selectRootItem } from '../../domain/reducers/fcp.reducer';

@UntilDestroy()
@Component({
    selector: 'app-fcp-closing-entries',
    templateUrl: 'fcpClosingEntries.component.html',
    styleUrls: ['fcpClosingEntries.component.scss'],
})
export class FcpClosingEntriesComponent implements OnInit {
    @Input() lastYear: boolean = false;
    @Input() currentUser: GetUserDto;
    @Input() dueDates: DueDates;
    @Input() showAll: boolean = false;
    private _domainModel?: FcpModel;

    @Input()
    set domainModel(value: FcpModel | undefined) {
        this._domainModel = value;
        // Perform the custom action when domainModel is set
        if (value) {
            this.onDomainModelSet(value);
        }
    }

    get domainModel(): FcpModel | undefined {
        return this._domainModel;
    }

    FcpTreeItemType = FcpTreeItemType; // Make enum accessible in template
    closingEntries$: Observable<ClosingEntry[]>;
    showForm: boolean = false;
    form: FormGroup;
    isFormFocused: boolean = false;
    editingEntryId: string | null = null;
    accounts: ClosingEntryAccount[] = [];
    isMenuOpenIndex: number | null = null;
    menuPosition = { top: 0, right: 0 };

    constructor(
        private readonly store: Store<FcpFeatureState>,
        private fb: FormBuilder,
        private readonly localizedTexts: I18NextService,
    ) {}

    ngOnInit() {
        this.initializeForm();
        if (this.showAll) {
            this.closingEntries$ = this.store.select(selectRootItem).pipe(
                map((item) => {
                    const entries = this.getAllClosingEntries(item);
                    const relevantDueDate = this.lastYear ? this.dueDates.dueDatePY : this.dueDates.dueDate;
                    return entries
                        .filter(
                            (closingEntry: ClosingEntry) =>
                                new Date(closingEntry.dueDate).getTime() === new Date(relevantDueDate).getTime(),
                        )
                        .sort((b, a) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                }),
            );
        } else {
            this.closingEntries$ = this.store.select(selectActiveTestItem).pipe(
                map((item) => {
                    const relevantDueDate = this.lastYear ? this.dueDates.dueDatePY : this.dueDates.dueDate;
                    return (item?.closingEntries || [])
                        .filter(
                            (closingEntry: ClosingEntry) =>
                                new Date(closingEntry.dueDate).getTime() === new Date(relevantDueDate).getTime(),
                        )
                        .sort((b, a) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
                }),
            );
        }
    }

    private getAllClosingEntries(item: FcpTreeItem): ClosingEntry[] {
        let entries = item.closingEntries || [];
        if (item.children && item.children.length > 0) {
            for (const child of item.children) {
                entries = entries.concat(this.getAllClosingEntries(child));
            }
        }
        return entries;
    }

    private initializeForm() {
        this.form = this.fb.group({
            text: ['', Validators.required], // Make text field required
            amount: [, Validators.required], // Make amount field required
            debit: ['', Validators.required], // Make debit field required
            credit: ['', Validators.required], // Make credit field required
        });
    }

    addClosingEntry() {
        if (this.form.invalid) {
            this.form.markAllAsTouched(); // Mark all fields as touched
            return; // Do not proceed if the form is invalid
        }
        this.showForm = false;
        const newClosingEntry: ClosingEntry = {
            id: this.editingEntryId ? this.editingEntryId : uuidV4(), // Use the existing id if editing
            createdAt: this.editingEntryId ? new Date() : new Date(),
            text: this.form.value.text,
            amount: this.form.value.amount,
            debit: this.form.value.debit,
            debitName: this.getNameForAccount(this.form.value.debit, this.accounts),
            credit: this.form.value.credit,
            creditName: this.getNameForAccount(this.form.value.credit, this.accounts),
            createdBy: this.currentUser.id,
            dueDate: this.dueDates.dueDate,
            booked: false,
        };

        if (this.editingEntryId) {
            this.store.dispatch(updateClosingEntry({ payload: newClosingEntry }));
        } else {
            this.store.dispatch(addClosingEntry({ payload: newClosingEntry }));
        }

        this.form.reset(); // Reset the form after submission
        this.isFormFocused = false;
        this.editingEntryId = null; // Clear the editing entry id
    }

    deleteClosingEntry(closingEntry: ClosingEntry) {
        this.isMenuOpenIndex = null;
        this.store.dispatch(deleteClosingEntry({ payload: closingEntry }));
    }

    updateClosingEntry(closingEntry: ClosingEntry) {
        this.isMenuOpenIndex = null;
        this.form.patchValue({
            text: closingEntry.text,
            amount: closingEntry.amount,
            debit: closingEntry.debit,
            credit: closingEntry.credit,
        });
        this.isFormFocused = true;
        this.showForm = true;
        this.editingEntryId = closingEntry.id; // Track the entry being edited
    }

    markAsBooked(value: boolean, closingEntry: ClosingEntry): void {
        const updatedComment = {
            ...closingEntry,
            booked: value,
        };
        this.store.dispatch(updateClosingEntry({ payload: updatedComment }));
    }

    onFormFocused() {
        this.isFormFocused = true;
    }

    onFormBlur() {
        if (!this.form.value.text) {
            this.isFormFocused = false;
        }
    }

    cancel() {
        this.showForm = false;
        this.form.reset();
        this.isFormFocused = false;
    }

    public showAddClosingEntryForm() {
        this.showForm = true;
    }

    toggleContextMenu(event: MouseEvent, index: number): void {
        event.stopPropagation();
        this.isMenuOpenIndex = this.isMenuOpenIndex === index ? null : index;

        // Get the button element
        const buttonElement = event.target as HTMLElement;

        // Get the bounding rectangle of the button element
        const rect = buttonElement.getBoundingClientRect();

        // Calculate the position of the dropdown at the bottom right of the button
        const right = window.innerWidth - rect.right;
        const top = rect.bottom;

        // Set the menu position
        this.menuPosition = { top: top, right: right };
    }

    closeContextMenu(): void {
        this.isMenuOpenIndex = null;
    }

    onMenuItemClick(action: string, item?: IAumMenuItem, event?: Event, entity?: any): void {
        if (action === 'DELETE') {
            this.deleteClosingEntry(entity as ClosingEntry);
        }
        if (action === 'EDIT') {
            this.updateClosingEntry(entity as ClosingEntry);
        }
    }

    private onDomainModelSet(value: FcpModel): void {
        console.log('Domain Model set:', value);

        // Get all accounts from the first and second inputFinStatements
        const allAccountsFromFirstTwo = this.getAllAccountsFromFirstTwoInputStatements(value);
        console.log('All Accounts from First Two Input Statements:', allAccountsFromFirstTwo);

        allAccountsFromFirstTwo.forEach((account) => {
            if (
                account.type === AccountType.BALANCE ||
                account.type === AccountType.INCOME ||
                account.type === AccountType.RECLASSES ||
                account.type === AccountType.UNCOVERED_SHORTFALL ||
                account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING
            ) {
                if (account.accountNumber) {
                    const closingEntryAccount: ClosingEntryAccount = {
                        name: account.name,
                        number: account.accountNumber,
                        id: account.type + account.accountNumber + account.name,
                        type: account.type,
                        metadata: '',
                    };
                    this.accounts.push(closingEntryAccount);
                }
            }
        });
    }

    private getAllAccountsFromFirstTwoInputStatements(domainModel: FcpModel): IAccount[] {
        const allAccounts: IAccount[] = [];

        // Check if there are at least two items in inputFinStatements
        if (domainModel.inputFinStatements && domainModel.inputFinStatements.length >= 2) {
            // Extract accounts from the template of the first item
            const firstTemplate = domainModel.inputFinStatements[0].templateAndAccountsMapping.template;
            if (firstTemplate) {
                this.collectAccountsFromTemplate(firstTemplate, allAccounts);
            }

            // Extract accounts from the template of the second item
            const secondTemplate = domainModel.inputFinStatements[1].templateAndAccountsMapping.template;
            if (secondTemplate) {
                this.collectAccountsFromTemplate(secondTemplate, allAccounts);
            }
        }

        return allAccounts;
    }

    private collectAccountsFromTemplate(template: IAccountMappingTemplate, allAccounts: IAccount[]): void {
        if (!template || !template.group) return;

        this.collectAccountsFromGroup(template.group, allAccounts);
    }

    private collectAccountsFromGroup(group: IAccountMappingTemplateGroup, allAccounts: IAccount[]): void {
        group.sortedItems.forEach((item) => {
            if (item.type === ItemType.FLI) {
                const fliItem = item as IAccountMappingTemplateGroupItemFLI;
                this.collectAccountsFromFLIItem(fliItem, allAccounts);
            } else if (item.hasOwnProperty('groups')) {
                const subGroup = item as unknown as IAccountMappingTemplateGroup;
                this.collectAccountsFromGroup(subGroup, allAccounts);
            }
        });
    }

    private collectAccountsFromFLIItem(fliItem: IAccountMappingTemplateGroupItemFLI, allAccounts: IAccount[]): void {
        fliItem.accounts.forEach((account) => {
            allAccounts.push(account); // Add the main account

            // Check if the account is a SubFLI
            if (isSubfli(account)) {
                const subfli = account as IAccountMappingSubFLI;
                // Recursively collect accounts from the subFLI
                this.collectAccountsFromSubFLI(subfli, allAccounts);
            }
        });
    }

    private collectAccountsFromSubFLI(subfli: IAccountMappingSubFLI, allAccounts: IAccount[]): void {
        subfli.accounts.forEach((account) => {
            allAccounts.push(account); // Add each account in the SubFLI

            // Recursively check for nested SubFLIs
            if (isSubfli(account)) {
                const nestedSubfli = account as IAccountMappingSubFLI;
                this.collectAccountsFromSubFLI(nestedSubfli, allAccounts);
            }
        });
    }

    private getNameForAccount(accountId: string, accounts: ClosingEntryAccount[]): string {
        if (!accountId) {
            return '';
        }
        if (!accounts || accounts.length === 0) {
            return accountId;
        }

        const account = accounts.find((acc) => acc.number === accountId);
        return account ? account.name : account + ' Unknown Account';
    }
}
